import rtlPlugin from "stylis-plugin-rtl";
import { useState, useEffect } from "react";
import { CacheProvider } from "@emotion/react";
import createCache from "@emotion/cache";
import { prefixer } from "stylis";
import { styled } from "@mui/material";
import { ButtonUnstyled, buttonUnstyledClasses } from "@mui/base";
import authStore from "@/stores/authStore";
import AccountingDetailsFilter from "@/components/accounting/accountingDetailsFilter";
import popupService from "@/services/popupService";
import api from "@/services/api";
import "./accountingStyles.css";
import { AccountingDetailsFilterRequest, ReceiptDetails, ReceiptDocumentInvoice } from "@/models/accounting";
import ArrowElement from "@/assets/images/ArrowElement.svg";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { formatDate, formatAmount, checkPermissionsWithAlert } from "@/util";
import { AccountingDocType } from "@/models/enums";
import { PermissionName } from "@/models/apiModels";
import AccountingReceiptTab from "./accountingReceiptTab";

const cacheLtr = createCache({
    key: "muiltr",
});

const cacheRtl = createCache({
    key: "muirtl",
    stylisPlugins: [prefixer, rtlPlugin],
});

const BackToDocsBtn = styled(ButtonUnstyled)(
    () => `
          &:hover {
            background-color: #222C4B;
          }
        
          &.${buttonUnstyledClasses.active} {
            background-color: #222C4B;
          }

          &.${buttonUnstyledClasses.root} {
            height: 40px;
            font-size: 16px;
            display: flex;
            align-items: center;
            gap: 7px;
          }
        
          &.${buttonUnstyledClasses.focusVisible} {
            box-shadow: 0 3px 20px 0 rgba(61, 71, 82, 0.1), 0 0 0 5px rgba(0, 127, 255, 0.5);
            outline: none;
          }
        
          &.${buttonUnstyledClasses.disabled} {
            opacity: 0.5;
            cursor: not-allowed;
          }`
);

const AccountingDocsReceipt = () => {
    const [receipt, setReceipt] = useState<ReceiptDetails>();
    const [accountingFilter, setAccountingFilter] = useState<AccountingDetailsFilterRequest>(
        new AccountingDetailsFilterRequest()
    );
    const [showAccountingTable, setShowAccountingTable] = useState(true);
    const [selectedReceipt, setSelectedReceipt] = useState<ReceiptDocumentInvoice | null>(null);

    const navigate = useNavigate();
    const { t } = useTranslation();
    const { id } = useParams();

    const onSearchClick = async () => {
        await setShowAccountingTable(false);
        startFetching();
        setShowAccountingTable(true);
    };

    const downloadPdfDocument = async (id: number) => {
        try {
            popupService.showLoading();
            const response = await api.downloadInvoiceDocumentPdf(id);
            if (response.data) {
                const fileName = api.parseFileName(response) ?? `Invoice_${id}.pdf`;
                const blob = new Blob([response.data], { type: "application/pdf" });
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = fileName;
                a.click();
                window.URL.revokeObjectURL(url);
            }

            popupService.close();
        } catch (err) {
            await popupService.showApiError(err);
            return;
        }
    };

    let startFetchingBusy = false;
    const startFetching = async () => {
        if (!checkPermissionsWithAlert(PermissionName.ListOfDocuments)) {
            return;
        }
        if (startFetchingBusy) {
            return;
        }
        try {
            startFetchingBusy = true;
            accountingFilter.receiptDocumentId = Number.parseInt(id ?? "");
            popupService.showLoading();
            const response = await api.getReceiptDetails(accountingFilter);
            setReceipt(response.data);
            popupService.close();
        } catch (err) {
            setReceipt(undefined);
            await popupService.showApiError(err);
            return;
        } finally {
            startFetchingBusy = false;
        }
    };

    async function sortBy(sortBy: string) {
        if (accountingFilter.orderBy !== sortBy) {
            setAccountingFilter((prevData) => ({
                ...prevData,
                orderAsc: true,
                orderBy: sortBy,
            }));
        } else {
            setAccountingFilter((prevData) => ({
                ...prevData,
                orderAsc: !accountingFilter.orderAsc,
                orderBy: sortBy,
            }));
        }
    }

    function getArrowClass(sort: string) {
        if (sort === accountingFilter.orderBy) {
            if (accountingFilter.orderAsc) {
                return "top-sort-arrow";
            } else {
                return "bottom-sort-arrow";
            }
        }
        return "icon-SortArrowIcon sort-arrow-icon";
    }

    useEffect(() => {
        let ignore = false;
        if (!ignore) {
            startFetching();
        }
        return () => {
            ignore = true;
        };
    }, [accountingFilter.orderBy, accountingFilter.orderAsc]);

    return (
        <CacheProvider value={authStore.isRtl ? cacheRtl : cacheLtr}>
            <div className="accounting-details-header">
                {selectedReceipt ? (
                    <AccountingReceiptTab selectedReceipt={selectedReceipt} receiptDetails={receipt} />
                ) : (
                    <div className="doc-info-container">
                        <div className="doc-info-column">
                            <div className="doc-info-row">
                                <div className="doc-info-label">{t("accounting.documentNumber")}:</div>
                            </div>
                            <div className="doc-info-row">
                                <div className="doc-info-label">{t("accounting.connectedInvoicesQTY")}:</div>
                            </div>
                        </div>
                        <div className="doc-info-column">
                            <div className="doc-info-row">
                                <div className="doc-info-value">{receipt?.documentNumber}</div>
                            </div>
                            <div className="doc-info-row">
                                <div className="doc-info-value">{receipt?.connectedInvoicesQty}</div>
                            </div>
                        </div>
                        <div className="doc-info-column">
                            <div className="doc-info-row">
                                <div className="doc-info-label">{t("accounting.documentType")}:</div>
                            </div>
                            <div className="doc-info-row">
                                <div className="doc-info-label">{t("accounting.docAmount")}:</div>
                            </div>
                        </div>
                        <div className="doc-info-column">
                            <div className="doc-info-row">
                                <div className="doc-info-value">{t(`enums.AccountingDocType.${receipt?.docType}`)}</div>
                            </div>
                            <div className="doc-info-row">
                                <div className="doc-info-value">
                                    {formatAmount(receipt?.total, receipt?.currencyId)}
                                </div>
                            </div>
                        </div>
                    </div>
                )}
                {selectedReceipt ? (
                    <div className="back-to-container">
                        <BackToDocsBtn className="basic-button" onClick={() => setSelectedReceipt(null)}>
                            <img className="back-to-list-arrow" src={ArrowElement} alt="ArrowElement" />
                            {t("accounting.button.backToAccountings")}
                        </BackToDocsBtn>
                    </div>
                ) : (
                    <div className="back-to-container">
                        <BackToDocsBtn className="basic-button" onClick={() => navigate("/accounting")}>
                            <img className="back-to-list-arrow" src={ArrowElement} alt="ArrowElement" />
                            {t("accounting.button.backToAccountings")}
                        </BackToDocsBtn>
                    </div>
                )}
            </div>
            <div className="accounting-container">
                <AccountingDetailsFilter
                    searchClick={onSearchClick}
                    filter={accountingFilter}
                    setFilter={setAccountingFilter}
                    docType={AccountingDocType.Receipt}
                />
                {showAccountingTable ? (
                    <div id="invoice-list-grid" className="employee-list-grid">
                        <table>
                            <thead id="orders-table-header">
                                <tr>
                                    <td>{t("orders.externalSystem")}</td>
                                    <td onClick={() => sortBy("OrderNumber")}>
                                        <div className="table-number-format-name">
                                            {t("accounting.invoiceNumber")}
                                            <i className={`icon-f ${getArrowClass("OrderNumber")} fs-12`} />
                                        </div>
                                    </td>
                                    <td onClick={() => sortBy("CreationDate")}>
                                        <div className="table-number-format-name">
                                            {t("accounting.invoiceDate")}
                                            <i className={`icon-f ${getArrowClass("CreationDate")} fs-12`} />
                                        </div>
                                    </td>
                                    <td>{t("accounting.to")}</td>
                                    <td>{t("accounting.for")}</td>
                                    <td>
                                        <div className="table-number-format-name">{t("accounting.amountNoVat")}</div>
                                    </td>
                                    <td>
                                        <div className="table-number-format-name">{t("accounting.vatAmount")}</div>
                                    </td>
                                    <td>
                                        <div className="table-number-format-name">{t("accounting.vatPercent")}</div>
                                    </td>
                                    <td>
                                        <div className="table-number-format-name">{t("accouting.totalInvoice")}</div>
                                    </td>
                                    <td>
                                        <div className="table-number-format-name">{t("accounting.download")}</div>
                                    </td>
                                </tr>
                            </thead>
                            <tbody>
                                {receipt?.invoices?.map((invoice) => (
                                    <tr key={invoice.id}>
                                        <td>{invoice.externalSystem}</td>
                                        <td>
                                            <div className="link-element table-number-format-value">
                                                <span onClick={() => navigate(`/accounting/invoice/${invoice.id}`)}>
                                                    {invoice.documentNumber}
                                                </span>
                                            </div>
                                        </td>
                                        <td>
                                            <div className="table-number-format-value">
                                                {formatDate(invoice.createdAt)}
                                            </div>
                                        </td>
                                        <td>{invoice.recipientName}</td>
                                        <td>{invoice.for}</td>
                                        <td>
                                            <div className="table-number-format-value">
                                                {formatAmount(invoice.excludeVatAmount, invoice.currencyId)}
                                            </div>
                                        </td>
                                        <td>
                                            <div className="table-number-format-value">
                                                {formatAmount(invoice.vatAmount, invoice.currencyId)}
                                            </div>
                                        </td>
                                        <td>
                                            <div className="table-number-format-value">
                                                {formatAmount(invoice.vatPercent)} {" %"}
                                            </div>
                                        </td>
                                        <td>
                                            <div className="table-number-format-value">
                                                {formatAmount(invoice.total, invoice.currencyId)}
                                            </div>
                                        </td>
                                        <td>
                                            <div className="table-number-format-value">
                                                <i
                                                    onClick={() => downloadPdfDocument(invoice.id)}
                                                    className="icon-f icon-export fs-20"
                                                />
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    ""
                )}
            </div>
        </CacheProvider>
    );
};

export default AccountingDocsReceipt;
